// Pure types + mappers for the Triage queue: stek-level issues awaiting a
// human decision, plus their RESOLVED counterparts (the revisitable decision
// records). "Unresolved external pool" was the first issue type; the derived
// MODEL issues (the BPMN-alignment engine's lint + consistency findings)
// arrive from the backend in four categories — misalignment, linking,
// consistency, hygiene — and are dismissable rather than resolvable: fixing
// the diagram makes them disappear from the evidence on their own.
import type { UnresolvedPoolRef } from "@/components/Applications/external-communication";
import { normalizePoolName } from "@/components/BPMN/pool-party-resolution";
import type { types } from "@/lib/openapi";
import type { PoolTriageDecision } from "@/stores/external-communication-store";

/**
 * An external pool in the process models that is not yet resolved to a
 * party, application, internal function or internal reference.
 */
export type UnresolvedPoolIssue = {
    type: "unresolved-external-pool";
    /** Stable issue id, also the `?issue=` deep-link value. */
    id: string;
    poolName: string;
    flowCount: number;
    /** Every process the pool appears in (the detail pane's picker). */
    processes: Array<{ id: string; name: string }>;
};

/**
 * A resolved pool: its ACTIVE decision record, kept in the rail (dimmed,
 * badged) so the decision can be revisited and undone — the reconcile
 * pattern. Carries the SAME id as the open issue, so resolving keeps the
 * selection: the pane flips from question to result box, no jump.
 */
export type ResolvedPoolIssue = {
    type: "resolved-external-pool";
    id: string;
    poolName: string;
    flowCount: number;
    /** Evidence snapshot from the record — keeps the pane renderable. */
    processes: Array<{ id: string; name: string }>;
    decision: PoolTriageDecision;
};

/**
 * A derived model issue from the BPMN-alignment engine (`get_triage_issues`):
 * a per-diagram lint finding or a cross-diagram consistency cluster. Its id
 * IS the backend issue key (`<issue_type>:<subject>`), also the `?issue=`
 * deep-link value. Dismissed issues keep their row (dimmed) so the dismissal
 * can be reopened.
 */
export type ModelIssue = {
    type: "model-issue";
    id: string;
    apiIssue: types.TriageIssue;
};

/** Union of every triage issue shape. */
export type TriageIssue = UnresolvedPoolIssue | ResolvedPoolIssue | ModelIssue;
export type TriageIssueType = TriageIssue["type"];

/** Queue presentation per issue type: group label, icon, explainer. */
export const TRIAGE_TYPE_META: Record<
    TriageIssueType,
    { label: string; icon: string; explainer: string }
> = {
    "unresolved-external-pool": {
        label: "Unresolved external pools",
        icon: "pi pi-question-circle",
        explainer:
            "Pools in the process models not yet resolved to an external " +
            "party, a system, an internal function or an internal reference.",
    },
    "resolved-external-pool": {
        label: "Resolved",
        icon: "pi pi-check-circle",
        explainer:
            "Decided pools — every decision is a revisitable record and " +
            "can be undone.",
    },
    "model-issue": {
        label: "Model issues",
        icon: "pi pi-flag",
        explainer:
            "Detected divergences between the process models and the BPMN " +
            "standard / house style.",
    },
};

/** Queue presentation per model-issue category, in display order. */
export const CATEGORY_META: Record<
    string,
    { label: string; icon: string; explainer: string }
> = {
    misalignment: {
        label: "BPMN misalignments",
        icon: "pi pi-exclamation-circle",
        explainer:
            "The diagram contradicts the BPMN standard: internal work in " +
            "external pools, external parties in lanes, human tasks in " +
            "system lanes.",
    },
    linking: {
        label: "Linking gaps",
        icon: "pi pi-link",
        explainer:
            "Model elements that name an entity without being linked to it " +
            "— system lanes bound through a stand-in role instead of the " +
            "application.",
    },
    consistency: {
        label: "Consistency",
        icon: "pi pi-arrows-h",
        explainer:
            "One thing under several spellings across diagrams, or names " +
            "that drifted apart.",
    },
    hygiene: {
        label: "Hygiene & style",
        icon: "pi pi-sparkles",
        explainer:
            "Style nudges: unlabeled decisions and message flows, empty " +
            "lanes, missing end events.",
    },
    runtime: {
        label: "Live behaviour",
        icon: "pi pi-bolt",
        explainer:
            "How the process actually runs, not how it's drawn — steps live " +
            "traffic never takes, paths a rollout hasn't reached. Talked " +
            "through with gotoko, not fixed with a diagram edit.",
    },
};

export const CATEGORY_ORDER = [
    "runtime",
    "misalignment",
    "linking",
    "consistency",
    "hygiene",
] as const;

/** Human copy for a decision's resolution, used by badges and result box. */
export const RESOLUTION_META: Record<string, { label: string; icon: string }> =
    {
        party: { label: "Party", icon: "pi pi-globe" },
        application: { label: "Application", icon: "pi pi-server" },
        internal_function: { label: "Role", icon: "pi pi-users" },
        internal_reference: { label: "Reference", icon: "pi pi-sitemap" },
    };

/**
 * Issue id for an unresolved pool: normalized so the chart's `?` nodes and
 * the queue agree on the deep link regardless of the pool name's casing.
 * Resolved issues reuse it, so a decision keeps its selection.
 */
export const unresolvedPoolIssueId = (poolName: string): string =>
    `unresolved-external-pool:${normalizePoolName(poolName)}`;

/** An unresolved pool (the map's untyped entry) as a triage issue. */
export const unresolvedPoolIssue = (
    pool: UnresolvedPoolRef
): UnresolvedPoolIssue => ({
    type: "unresolved-external-pool",
    id: unresolvedPoolIssueId(pool.name),
    poolName: pool.name,
    flowCount: pool.flow_count,
    processes: (pool.processes ?? []).map((ref) => ({
        id: ref.id,
        name: ref.name,
    })),
});

/** An active decision record as the pool's resolved issue. */
export const resolvedPoolIssue = (
    decision: PoolTriageDecision
): ResolvedPoolIssue => ({
    type: "resolved-external-pool",
    id: unresolvedPoolIssueId(decision.pool_name),
    poolName: decision.pool_name,
    flowCount: decision.flow_count ?? 0,
    processes: (decision.processes ?? []).map((ref) => ({
        id: ref.id,
        name: ref.name,
    })),
    decision,
});

/** A backend-derived model issue as a queue issue. */
export const modelIssue = (apiIssue: types.TriageIssue): ModelIssue => ({
    type: "model-issue",
    id: apiIssue.key,
    apiIssue,
});

/** The derived issue types that continue a pool decision's stack (step 2 —
 *  the diagrams still model the decided pool). Their `subject` is the
 *  backend-normalized pool name, the key that stitches the one story across
 *  the resolved row, the open issue row and the unified pane. */
export const isRestructureIssueType = (issueType: string): boolean =>
    issueType === "internal-actor-as-pool" ||
    issueType === "internal-system-as-pool";

/** Rail-row presentation (name + one-line summary + icon) per issue shape. */
export const issueDisplay = (
    issue: TriageIssue
): { name: string; summary: string; icon: string } => {
    if (issue.type === "model-issue") {
        const api = issue.apiIssue;
        const count = api.occurrences?.length ?? 0;
        const firstProcess = api.occurrences?.find(
            (occurrence) => occurrence.process
        )?.process;
        const parts = [
            count === 1 ? "1 occurrence" : `${count} occurrences`,
            ...(firstProcess ? [firstProcess.name] : []),
        ];
        return {
            name: api.title,
            summary: parts.join(" · "),
            icon:
                CATEGORY_META[api.category]?.icon ??
                TRIAGE_TYPE_META["model-issue"].icon,
        };
    }
    return {
        name: issue.poolName,
        summary: "",
        icon: TRIAGE_TYPE_META[issue.type].icon,
    };
};

/** One-line badge text for a resolved row: "Party · Atradius". */
export const resolutionBadge = (decision: PoolTriageDecision): string => {
    const meta = RESOLUTION_META[decision.resolution];
    const label = meta?.label ?? decision.resolution;
    if (decision.resolution === "internal_function") {
        const count = decision.role_ids?.length ?? 0;
        return count > 1 ? `${label} · ${count} domains` : label;
    }
    return decision.target?.name ? `${label} · ${decision.target.name}` : label;
};
